import { CircularProgress } from "@material-ui/core";
import React, {
  forwardRef,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useContext,
  useState,
} from "react";
import { useNavigate, useParams } from "react-router";
import { useRecoilValue } from "recoil";
import { DrawerContext } from "../../../../components/DrawerProvider/DrawerProvider";
import Map from "../../../../../../components/Map/Map";
import { MeterSystem } from "../../../../../../recoils/MeterSystemRecoil";
import {
  FullPivotList,
  ListLatestStreamPivot,
  PivotLight,
} from "../../../../../../recoils/PivotRecoil";
import { Irpd } from "../../../../../../redux/irpds/types";
import routes from "../../../../../../routes/routes";
import {
  instanceOfBauerPivotShape,
  instanceOfIrpdShape,
  instanceOfLinearPivotShapeShape,
  instanceOfMeterSystemShape,
  instanceOfPivotShape,
  instanceOfRepeaterShape,
  MapShape,
} from "../../../../../../utils/types";

import { addIrpdOnMap } from "./addIrpdOnMaps";
import { addMeterSystemsOnMap } from "./addMeterSystemsOnMap";
import { addPivotsOnMap } from "./addPivotsOnMaps";
import { addBauerPivotsOnMap } from "./addBauerPivotsOnMaps";
import { Repeater } from "../../../../../../recoils/RepeaterRecoil";
import { addRepeaterOnMap } from "./addRepeaterOnMaps";
import {
  BauerPivotObj,
  FarmBauerPivotObj,
} from "../../../../../../redux/bauerPivot/types";
import { i18n } from "../../../../../../i18n/i18nText";
import {
  isValidIrrigationRemainingTime,
  convertIrrigationRemainingTimeToHoursMinutes,
  isOldProtocol,
  isV5Protocol,
  PIVOT_STATUS_COLOR_OLD_BLUE,
  PIVOT_STATUS_COLOR_OLD_BROWN,
  PIVOT_STATUS_COLOR_OLD_GREEN,
  PIVOT_STATUS_COLOR_OLD_LIGHTBLUE,
  PIVOT_STATUS_COLOR_OLD_ORANGE,
  PIVOT_STATUS_COLOR_OLD_RED,
  PIVOT_STATUS_COLOR_OLD_YELLOW,
  PIVOT_STATUS_COLOR_V5_BLUE,
  PIVOT_STATUS_COLOR_V5_BROWN,
  PIVOT_STATUS_COLOR_V5_GREEN,
  PIVOT_STATUS_COLOR_V5_RED,
  PIVOT_STATUS_COLOR_V5_SLAVE_BLUE1,
  PIVOT_STATUS_COLOR_V5_YELLOW,
} from "../../../../../../utils/models/pivots";
import { IRRIFAST_COLORS } from "../../../../Pivot/SelectedPivot/components/IrrifastBox";
import { TYPE } from "../../../../Pivot/EditPivot/components/EditPivotFormV5/NewEditPivotV5";
import {
  bauerPivotStatus,
  colorStatus,
} from "../../../../../../utils/models/bauerpivots";
import blueMarker from "../../../../../../assets/images/markers/blue.svg";
import brownMarker from "../../../../../../assets/images/markers/brown.svg";
import grayMarker from "../../../../../../assets/images/markers/gray.svg";
import greenMarker from "../../../../../../assets/images/markers/green.svg";
import lightblueMarker from "../../../../../../assets/images/markers/lightblue.svg";
import orangeMarker from "../../../../../../assets/images/markers/orange.svg";
import redMarker from "../../../../../../assets/images/markers/red.svg";
import slaveBlue1Marker from "../../../../../../assets/images/markers/slaveBlue1.svg";
import yellowMarker from "../../../../../../assets/images/markers/yellow.svg";

// Assets
interface Props {
  pivotsLight?: PivotLight[];
  bauerPivots?: FarmBauerPivotObj[];
  irpds?: Irpd[];
  meterSystems?: MeterSystem[];
  repeaters?: Repeater[];
  draggable: boolean;
  disableUI?: boolean;
  latestPanelStream?: any;
  latestGPSStream?: any;
  disableFullScreen?: boolean;
  clickOnMapToToggleFullscreen?: boolean;
  zoomScroll?: boolean;
  isTabSelected?: number;
  zoom?: number;
  infoWindow?: boolean;
  isImperativeControlled?: boolean;
}

export interface FarmMapHandle {
  drawPivotsOnMap: () => any;
  updateDevicesWithStreams: () => any;
}

function FarmMap(props: Props, ref) {
  const {
    pivotsLight,
    bauerPivots,
    draggable,
    irpds,
    meterSystems,
    repeaters,
    latestGPSStream,
    latestPanelStream,
    disableUI,
    zoomScroll,
    isTabSelected,
    zoom,
    infoWindow,
  } = props;
  const googleMapRef = useRef(undefined);
  const [mapShapes, setMapShapes] = useState<MapShape[]>([]);
  const navigate = useNavigate();
  const farmID: number = parseInt(useParams().farm, 10);
  const pivotID: number = parseInt(useParams().pivot, 10);
  const [disableQuickSelect, setDisableQuickSelect] = useState<boolean>(false);
  const [currentDrawing, setCurrentDrawing] = useState<boolean>(false);

  const listStreamStatus = useRecoilValue(ListLatestStreamPivot);
  const streamStatusComplete = useMemo(() => {
    if (!listStreamStatus) return;
    return listStreamStatus.filter((p) =>
      pivotsLight ? pivotsLight.find((pL) => pL.id == p.id) : null
    );
  }, [listStreamStatus, pivotID]);

  const pivots = useRecoilValue(FullPivotList);
  const pivotsCompleted = useMemo(() => {
    if (!pivots) return;
    return pivots.filter((p) =>
      pivotsLight ? pivotsLight.find((pL) => pL.id == p.id) : null
    );
  }, [pivots, pivotsLight]);

  const bauerPivotsStatusKey = useMemo(() => {
    if (!bauerPivots || bauerPivots.length === 0) return "";
    return bauerPivots
      .map((bp) => `${bp.id}:${bp.latest?.status || "null"}`)
      .join("|");
  }, [bauerPivots]);

  const meterSystemsStatusKey = useMemo(() => {
    if (!meterSystems || meterSystems.length === 0) return "";
    const {
      getIMeterEventStreamStatus,
    } = require("../../../../../../utils/models/imeter");
    const moment = require("moment");

    return meterSystems
      .map((ms) => {
        const meterSystemData = ms.imeter_set?.[0];
        if (!meterSystemData) return `${ms.id}:null`;

        const eventStreamCreated = meterSystemData.latest_event_stream?.created;
        const periodicStreamCreated =
          meterSystemData.latest_periodic_stream?.created;

        const getStatusFromStream = (stream: any) => {
          return stream?.content?.imanage_master_status?.status;
        };

        const eventStreamStatus = getStatusFromStream(
          meterSystemData.latest_event_stream
        );
        const periodicStreamStatus = getStatusFromStream(
          meterSystemData.latest_periodic_stream
        );

        const statusToUse =
          eventStreamCreated &&
          periodicStreamCreated &&
          moment(eventStreamCreated).isSameOrAfter(
            moment(periodicStreamCreated)
          )
            ? eventStreamStatus
            : periodicStreamStatus;

        const finalStatus = statusToUse ?? periodicStreamStatus ?? 0;
        return `${ms.id}:${finalStatus}`;
      })
      .join("|");
  }, [meterSystems]);

  const bounds = useMemo(() => {
    const mapBounds = new window.google.maps.LatLngBounds();
    if (mapShapes?.length == 0) return undefined;
    mapShapes.forEach((shape) => {
      if (!shape) return;
      if (instanceOfPivotShape(shape)) {
        mapBounds.union(shape.circle.getBounds());
      }
      if (instanceOfBauerPivotShape(shape)) {
        mapBounds.union(shape.circle.getBounds());
      }
      if (instanceOfIrpdShape(shape)) {
        mapBounds.union(shape.circle.getBounds());
      }
      if (instanceOfMeterSystemShape(shape)) {
        mapBounds.union(shape.circle.getBounds());
      }
      if (instanceOfLinearPivotShapeShape(shape)) {
        mapBounds.union(shape.circle.getBounds());
      }
      if (instanceOfRepeaterShape(shape)) {
        mapBounds.union(shape.circle.getBounds());
      }
    });
    return mapBounds;
  }, [mapShapes]);

  const handleOnGoogleApiLoaded = useCallback(
    (maps: { map: any; maps: any }) => {
      if (!googleMapRef.current || googleMapRef.current != maps.map) {
        googleMapRef.current = maps.map;
        doMapDraw();
        googleMapRef.current.fitBounds(bounds);
      }
    },
    [pivotsCompleted, bauerPivots, irpds, meterSystems, repeaters]
  );

  const ctx = useContext(DrawerContext);

  useEffect(() => {
    function navigateToPivot(
      e: CustomEvent<{ pivotId: string; farmId: string }>
    ) {
      const { farmId, pivotId } = e.detail;
      ctx.setFarmID(farmId);
      ctx.setPivotID(pivotId);
      navigate(
        routes.DASHBOARD.SELECTED_PIVOT.replace(":farm", farmId).replace(
          ":pivot",
          pivotId
        )
      );
    }

    window.addEventListener("navigateToPivot", navigateToPivot);

    function navigateToBauerPivot(
      e: CustomEvent<{ pivotId: string; farmId: string }>
    ) {
      const { farmId, pivotId } = e.detail;
      ctx.setFarmID(farmId);
      ctx.setPivotID(pivotId);
      navigate(
        routes.DASHBOARD.SELECTED_BAUER_PIVOT.replace(":farm", farmId).replace(
          ":pivot",
          pivotId
        )
      );
    }

    window.addEventListener("navigateToBauerPivot", navigateToBauerPivot);

    function navigateToIrpd(
      e: CustomEvent<{ irpdId: string; farmId: string }>
    ) {
      const { farmId, irpdId } = e.detail;
      navigate(
        routes.DASHBOARD.SELECTED_IRPD.replace(":farm", farmId).replace(
          ":irpd",
          irpdId
        )
      );
    }

    window.addEventListener("navigateToIrpd", navigateToIrpd);

    function navigateToMeterSystem(
      e: CustomEvent<{ meterSystemId: string; farmId: string }>
    ) {
      const { farmId, meterSystemId } = e.detail;
      navigate(
        routes.DASHBOARD.SELECTED_METER_SYSTEM.replace(":farm", farmId).replace(
          ":metersystem",
          meterSystemId
        )
      );
    }

    window.addEventListener("navigateToMeterSystem", navigateToMeterSystem);

    function navigateToRepeater(
      e: CustomEvent<{ repeaterId: string; farmId: string }>
    ) {
      const { farmId, repeaterId } = e.detail;
      navigate(
        routes.DASHBOARD.EDIT_REPEATER.replace(":farm", farmId).replace(
          ":repeater",
          repeaterId
        )
      );
    }

    window.addEventListener("navigateToRepeater", navigateToRepeater);

    return () => {
      window.removeEventListener("navigateToPivot", navigateToPivot);
      window.removeEventListener("navigateToBauerPivot", navigateToBauerPivot);
      window.removeEventListener("navigateToIrpd", navigateToIrpd);
      window.removeEventListener(
        "navigateToMeterSystem",
        navigateToMeterSystem
      );
      window.removeEventListener("navigateToRepeater", navigateToRepeater);
    };
  }, []);

  const totalPivotLength = pivotsLight?.length;
  const pivotsAlreadyLoadedLength = pivotsCompleted
    ? pivotsCompleted.filter((p) => p.config || p.controllerconfig).length
    : 0;

  const updateDevicesWithStreamData = useCallback(() => {
    if (!googleMapRef.current || mapShapes.length === 0) return;

    mapShapes.forEach((shape) => {
      if (!shape) return;

      if (instanceOfPivotShape(shape)) {
        const pivotId = (shape as any).pivotId;
        if (pivotId) {
          const updatedPivot = pivotsCompleted?.find((p) => p.id === pivotId);
          const streamStatus = streamStatusComplete?.find(
            (s) => s.id === pivotId
          );

          if (updatedPivot && streamStatus) {
            if (
              updatedPivot.controllerstream_gps?.content?.latitude &&
              updatedPivot.controllerstream_gps?.content?.longitude
            ) {
              const newPosition = new google.maps.LatLng(
                updatedPivot.controllerstream_gps.content.latitude,
                updatedPivot.controllerstream_gps.content.longitude
              );
              shape.circle.setCenter(newPosition);
            }

            if (streamStatus.streamStatus !== undefined) {
              const statusColor = streamStatus.streamStatus.color;

              shape.circle.setOptions({
                fillColor: statusColor,
                strokeColor: statusColor,
              });

              if (shape.sector) {
                shape.sector.setOptions({
                  fillColor: statusColor,
                  strokeColor: statusColor,
                });
              }

              if ((shape as any).marker) {
                let markerIconUrl = "";

                if (isV5Protocol(updatedPivot)) {
                  switch (statusColor) {
                    case PIVOT_STATUS_COLOR_V5_RED:
                      markerIconUrl = redMarker;
                      break;
                    case PIVOT_STATUS_COLOR_V5_GREEN:
                      markerIconUrl = greenMarker;
                      break;
                    case PIVOT_STATUS_COLOR_V5_BLUE:
                      markerIconUrl = blueMarker;
                      break;
                    case PIVOT_STATUS_COLOR_V5_BROWN:
                      markerIconUrl = brownMarker;
                      break;
                    case PIVOT_STATUS_COLOR_V5_YELLOW:
                      markerIconUrl = yellowMarker;
                      break;
                    case PIVOT_STATUS_COLOR_V5_SLAVE_BLUE1:
                      markerIconUrl = slaveBlue1Marker;
                      break;
                    default:
                      markerIconUrl = grayMarker;
                      break;
                  }
                } else if (isOldProtocol(updatedPivot)) {
                  switch (statusColor) {
                    case PIVOT_STATUS_COLOR_OLD_RED:
                      markerIconUrl = redMarker;
                      break;
                    case PIVOT_STATUS_COLOR_OLD_BLUE:
                      markerIconUrl = blueMarker;
                      break;
                    case PIVOT_STATUS_COLOR_OLD_GREEN:
                      markerIconUrl = greenMarker;
                      break;
                    case PIVOT_STATUS_COLOR_OLD_YELLOW:
                      markerIconUrl = yellowMarker;
                      break;
                    case PIVOT_STATUS_COLOR_OLD_ORANGE:
                      markerIconUrl = orangeMarker;
                      break;
                    case PIVOT_STATUS_COLOR_OLD_LIGHTBLUE:
                      markerIconUrl = lightblueMarker;
                      break;
                    case PIVOT_STATUS_COLOR_OLD_BROWN:
                      markerIconUrl = brownMarker;
                      break;
                    case PIVOT_STATUS_COLOR_V5_SLAVE_BLUE1:
                      markerIconUrl = slaveBlue1Marker;
                      break;
                    default:
                      markerIconUrl = grayMarker;
                      break;
                  }
                } else {
                  markerIconUrl = grayMarker;
                }

                try {
                  (shape as any).marker.setIcon({ url: markerIconUrl });
                } catch (error) {
                  console.error("Error updating Pivot marker icon:", error);
                }
              }
            }

            if ((shape as any).infowindow) {
              const newContent = generatePivotInfoWindowContent(
                updatedPivot,
                streamStatus,
                farmID
              );
              (shape as any).infowindow.setContent(newContent);
            }
          }
        }
      }

      if (instanceOfIrpdShape(shape)) {
        const irpdId = (shape as any).irpdId;
        if (irpdId) {
          const updatedIrpd = irpds?.find((i) => i.id === irpdId);
          if (updatedIrpd) {
            if (updatedIrpd.position) {
              const [lat, lng] = updatedIrpd.position.split(",").map(Number);
              const newPosition = new google.maps.LatLng(lat, lng);
              shape.circle.setCenter(newPosition);
            }

            if ((shape as any).marker) {
              const {
                getIrpdStreamStatus,
              } = require("../../../../../../utils/models/irpds");
              const { getStatusValue } = require("./addIrpdOnMaps");

              const statusValue = getStatusValue(updatedIrpd);
              const irpdStatus = getIrpdStreamStatus(statusValue);

              if (irpdStatus.path_image) {
                try {
                  (shape as any).marker.setIcon({ url: irpdStatus.path_image });
                } catch (error) {
                  console.error("Error updating Irpd marker icon:", error);
                }
              }
            }

            if ((shape as any).infowindow) {
              const newContent = generateIrpdInfoWindowContent(
                updatedIrpd,
                farmID
              );
              (shape as any).infowindow.setContent(newContent);
            }
          }
        }
      }

      if (instanceOfMeterSystemShape(shape)) {
        const meterSystemId = (shape as any).meterSystemId;
        if (meterSystemId) {
          const updatedMeterSystem = meterSystems?.find(
            (m) => m.id === meterSystemId
          );
          if (updatedMeterSystem) {
            if (updatedMeterSystem.imeter_set?.[0]?.position) {
              const [
                lat,
                lng,
              ] = updatedMeterSystem.imeter_set[0].position
                .split(",")
                .map(Number);
              const newPosition = new google.maps.LatLng(lat, lng);
              shape.circle.setCenter(newPosition);
            }

            const statusColor = getMeterSystemStatusColor(updatedMeterSystem);
            shape.circle.setOptions({
              fillColor: statusColor,
              strokeColor: statusColor,
            });

            if ((shape as any).marker) {
              const {
                getIMeterEventStreamStatus,
              } = require("../../../../../../utils/models/imeter");
              const moment = require("moment");

              const meterSystemData = updatedMeterSystem.imeter_set?.[0];
              if (meterSystemData) {
                const eventStreamCreated =
                  meterSystemData.latest_event_stream?.created;
                const periodicStreamCreated =
                  meterSystemData.latest_periodic_stream?.created;

                const status = eventStreamCreated
                  ? getIMeterEventStreamStatus(
                      moment(eventStreamCreated).isSameOrAfter(
                        moment(periodicStreamCreated)
                      )
                        ? meterSystemData.latest_event_stream.content
                            .imanage_master_status.status
                        : meterSystemData.latest_periodic_stream.content
                            .imanage_master_status.status
                    )
                  : getIMeterEventStreamStatus(
                      meterSystemData.latest_periodic_stream.content
                        .imanage_master_status.status
                    );

                const textPercentage = `${Math.round(
                  meterSystemData.water_level_percentage
                )}%`;
                const waterHeightPercentage = `${(
                  100 - meterSystemData.water_level_percentage
                ).toFixed()}%`;
                const maxLimit = meterSystemData.latest_config?.max_limit;
                const minLimit = meterSystemData.latest_config?.min_limit;
                const lakeLevel = Math.round(
                  meterSystemData.water_level_percentage
                );

                const iconColor =
                  maxLimit !== 0 && minLimit !== 0
                    ? lakeLevel >= maxLimit
                      ? "#ff0000"
                      : lakeLevel <= minLimit
                      ? "#ffa500"
                      : "#4679ed"
                    : "#4679ed";

                const renderSvgUrl = (
                  textPercentage: string,
                  waterHeightPercentage: string,
                  iconColor: string
                ) => {
                  const source = `
                  <svg xmlns="http://www.w3.org/2000/svg" xmlns:xlink="http://www.w3.org/1999/xlink" width="55" height="55">
                  <defs>
                  <linearGradient id="a" x1="50%" x2="50%" y1="0%" y2="100%">
                  <stop offset="${waterHeightPercentage}" stop-color="#808080"/>
                  <stop offset="0%" stop-color="${iconColor}"/>
                  </linearGradient>
                  <linearGradient xlink:href="#a" id="b" spreadMethod="repeat"/>
                  </defs>
                  <circle cx="26" cy="26" r="25" fill="url(#b)" opacity="0.8" stroke="#808080" stroke-width="1"/>
                  <text x="12" y="32" fill="#fff" font-family="Arial" font-weight="normal" font-size="16">${textPercentage}</text>
                  </svg>`;

                  return "data:image/svg+xml;base64," + btoa(source);
                };

                const newIconUrl = renderSvgUrl(
                  textPercentage,
                  waterHeightPercentage,
                  iconColor
                );

                try {
                  (shape as any).marker.setIcon({ url: newIconUrl });
                } catch (error) {
                  console.error(
                    "Error updating MeterSystem marker icon:",
                    error
                  );
                }
              }
            }

            if ((shape as any).infowindow) {
              const newContent = generateMeterSystemInfoWindowContent(
                updatedMeterSystem,
                farmID
              );
              (shape as any).infowindow.setContent(newContent);
            }
          }
        }
      }

      if (instanceOfBauerPivotShape(shape)) {
        const bauerPivotId = (shape as any).bauerPivotId;
        if (bauerPivotId) {
          const updatedBauerPivot = bauerPivots?.find(
            (bp) => bp.id === bauerPivotId
          );
          if (updatedBauerPivot && updatedBauerPivot.latest) {
            const statusColor = colorStatus(updatedBauerPivot.latest.status);

            if (shape.circle) {
              shape.circle.setOptions({
                fillColor: statusColor,
                strokeColor: statusColor,
              });
            }

            if (shape.sector) {
              shape.sector.setOptions({
                fillColor: statusColor,
                strokeColor: statusColor,
              });
            }
            if ((shape as any).marker) {
              let markerIconUrl = "";
              const normalizedColor = (statusColor || "").toLowerCase().trim();

              if (normalizedColor === "#e92400") {
                markerIconUrl = redMarker;
              } else if (normalizedColor === "#207833") {
                markerIconUrl = greenMarker;
              } else if (normalizedColor === "#4679ed") {
                markerIconUrl = blueMarker;
              } else if (normalizedColor === "#ffd700") {
                markerIconUrl = yellowMarker;
              } else if (normalizedColor === "#ff8c00") {
                markerIconUrl = orangeMarker;
              } else {
                markerIconUrl = grayMarker;
              }

              try {
                (shape as any).marker.setIcon({ url: markerIconUrl });
              } catch (error) {
                console.error("Error updating BauerPivot marker icon:", error);
              }
            }

            if ((shape as any).infowindow) {
              const newContent = generateBauerPivotInfoWindowContent(
                updatedBauerPivot,
                farmID
              );
              (shape as any).infowindow.setContent(newContent);
            }
          }
        }
      }

      if (instanceOfRepeaterShape(shape)) {
        const repeaterId = (shape as any).repeaterId;
        if (repeaterId) {
          const updatedRepeater = repeaters?.find((r) => r.id === repeaterId);
          if (updatedRepeater) {
            if (updatedRepeater.position) {
              const [lat, lng] = updatedRepeater.position
                .split(",")
                .map(Number);
              const newPosition = new google.maps.LatLng(lat, lng);
              shape.circle.setCenter(newPosition);
            }

            if ((shape as any).infowindow) {
              const newContent = generateRepeaterInfoWindowContent(
                updatedRepeater,
                farmID
              );
              (shape as any).infowindow.setContent(newContent);
            }
          }
        }
      }
    });
  }, [
    mapShapes,
    pivotsCompleted,
    streamStatusComplete,
    irpds,
    meterSystems,
    repeaters,
    bauerPivots,
    farmID,
  ]);

  const generatePivotInfoWindowContent = (
    pivot: any,
    streamStatus: any,
    farmId: number
  ) => {
    let towersInnerHtml = "";
    if (pivot.is_irrifast && pivot.irrifast_tower_status?.length > 0) {
      towersInnerHtml = pivot.irrifast_tower_status
        ?.map(
          (tower) => `
        <span style="background:${
          IRRIFAST_COLORS[
            tower.aligment_status !== null ? tower.aligment_status : 2
          ]
        };width:32px;height:32px;border-radius:4px;color:#ffffff;text-align:center;font-weight:bold;padding-top:6px;">
            ${tower.tower_number.toString().padStart(2, "0")}
          </span>
      `
        )
        .join("");
    }

    const isLPM = pivot.automation_type === TYPE.LINEAR_PIVOT_MONITOR;

    if (isLPM) {
      return `<span 
        style="cursor: pointer;"
        onclick="(function (){
          let event = new CustomEvent('navigateToPivot', { detail: {
            pivotId: ${pivot.id},
            farmId: ${farmId}
          }});

          window.dispatchEvent(event);
        })()">
          <span style='font-weight:600;font-size:.7rem;margin:4px 0 4px 8px;padding:4px 8px;border-radius:8px;color:#000;text-align: left'>
            ${pivot.name}
          </span>
          
          <div
            style='background-color:${
              streamStatus?.streamStatus?.color || "#808080"
            };font-weight:600;font-size:.7rem;margin:4px 0 4px 8px;padding:4px 8px;border-radius:8px;color:#fff;text-align: center;'
          >
            ${streamStatus?.streamStatus?.text || "N/A"}
            ${
              streamStatus?.streamStatus?.number === 4 &&
              streamStatus?.irrigation_remaining_time &&
              isValidIrrigationRemainingTime(
                streamStatus?.irrigation_remaining_time
              )
                ? convertIrrigationRemainingTimeToHoursMinutes(
                    streamStatus?.irrigation_remaining_time
                  )
                : ""
            }
          </div>
          <p style='font-size:.7rem;margin:4px 0 4px 8px;padding:4px 8px;border-radius:8px;color:#000;display:flex;flex-direction:row;flex-wrap:wrap;text-align: left'>
            ${i18n("LPM_EFFICIENCY")} 
            <b>${
              " " +
              (
                (pivot?.controllerstream_panel?.content?.operation_time
                  ?.wet_hour /
                  pivot?.controllerstream_panel?.content?.operation_time
                    ?.total_hour) *
                100
              )?.toFixed(2)
            }%
            </b>
          </p>
        </span>`;
    } else {
      return `<span 
        style="cursor: pointer;display:flex;flex-direction:column;justify-content:center;align-items:center;"
        onclick="(function (){
          let event = new CustomEvent('navigateToPivot', { detail: {
            pivotId: ${pivot.id},
            farmId: ${farmId}
          }});

          window.dispatchEvent(event);
        })()">
          <span style='font-weight:600;font-size:.7rem;margin:4px 0 4px 0px;padding:4px 8px;border-radius:8px;color:#000;display:flex;flex-direction:row;flex-wrap:wrap;'>
            ${pivot.name}
          </span>
          <div
            style='background-color:${
              streamStatus?.streamStatus?.color || "#808080"
            };font-weight:600;font-size:.7rem;margin:4px 0 4px 0px;padding:4px 8px;border-radius:8px;color:#fff;display:flex;flex-direction:row;flex-wrap:wrap;'
          >
            ${streamStatus?.streamStatus?.text || "N/A"}
            ${
              streamStatus?.streamStatus?.number === 4 &&
              streamStatus?.irrigation_remaining_time &&
              isValidIrrigationRemainingTime(
                streamStatus?.irrigation_remaining_time
              )
                ? convertIrrigationRemainingTimeToHoursMinutes(
                    streamStatus?.irrigation_remaining_time
                  )
                : ""
            }
          </div>
          ${
            pivot.is_irrifast && pivot.irrifast_tower_status?.length > 0
              ? `
                <div style="display:flex;flex-wrap:wrap;gap:4px;max-width:180px;margin-top:16px;margin-bottom:16px;">
                  ${towersInnerHtml}
                </div>
              `
              : ""
          }
        </span>`;
    }
  };

  const generateIrpdInfoWindowContent = (irpd: any, farmId: number) => {
    const {
      getIrpdStreamStatus,
    } = require("../../../../../../utils/models/irpds");
    const { getStatusValue } = require("./addIrpdOnMaps");

    const statusValue = getStatusValue(irpd);
    const status = getIrpdStreamStatus(statusValue);

    return `<span 
      style="cursor:pointer;display:flex;flex-direction:column;justify-content:center;align-items:center;"
      onclick="(function (){
        let event = new CustomEvent('navigateToIrpd', { detail: {
          pivotId: ${irpd.id},
          farmId: ${farmId}
        }});
        window.dispatchEvent(event);
      })()">
        <span style='font-weight:600;font-size:.7rem;margin:4px 0 4px 8px;padding:4px 8px;border-radius:8px;color:#000;text-align: left'>
          ${irpd.name}
        </span>
        <div
          style='background-color:${status.color};font-weight:600;font-size:.7rem;margin:4px 0 4px 8px;padding:4px 8px;border-radius:8px;color:#fff;text-align: center;'
        >
          ${status.text}
        </div>
      </span>`;
  };

  const generateMeterSystemInfoWindowContent = (
    meterSystem: any,
    farmId: number
  ) => {
    const {
      getIMeterEventStreamStatus,
    } = require("../../../../../../utils/models/imeter");
    const moment = require("moment");

    const meterSystemData = meterSystem.imeter_set?.[0];
    if (!meterSystemData) return `<div>Meter System N/A</div>`;

    const eventStreamCreated = meterSystemData.latest_event_stream?.created;
    const periodicStreamCreated =
      meterSystemData.latest_periodic_stream?.created;

    const getStatusFromStream = (stream: any) => {
      return stream?.content?.imanage_master_status?.status;
    };

    const eventStreamStatus = getStatusFromStream(
      meterSystemData.latest_event_stream
    );
    const periodicStreamStatus = getStatusFromStream(
      meterSystemData.latest_periodic_stream
    );

    const statusToUse =
      eventStreamCreated &&
      periodicStreamCreated &&
      moment(eventStreamCreated).isSameOrAfter(moment(periodicStreamCreated))
        ? eventStreamStatus
        : periodicStreamStatus;

    const finalStatus = statusToUse ?? periodicStreamStatus ?? 0;

    const statusColor =
      finalStatus !== undefined && finalStatus !== null
        ? getIMeterEventStreamStatus(finalStatus).color
        : getIMeterEventStreamStatus(0).color;

    const validatedLastStatus = finalStatus ?? 0;

    const statusText = getIMeterEventStreamStatus(validatedLastStatus).text;

    return `<span 
      style="cursor: pointer;"
      onclick="(function (){
        let event = new CustomEvent('navigateToMeterSystem', { detail: {
          pivotId: ${meterSystemData.id},
          farmId: ${farmId}
        }});

        window.dispatchEvent(event);
      })()">
        <span style='font-weight:600;font-size:.7rem;margin:4px 0 4px 8px;padding:4px 8px;border-radius:8px;color:#000;display:flex;flex-direction:row;flex-wrap:wrap;'>
        ${`${meterSystemData.name || "Meter System"}`}
        </span>
            <div
              style='background-color:${statusColor};font-weight:600;font-size:.7rem;margin:4px 0 4px 8px;padding:4px 8px;border-radius:8px;color:#fff;display:flex;flex-direction:row;flex-wrap:wrap;justify-content:center;'
                >
                ${statusText}
              </div>
      </span>`;
  };

  const generateRepeaterInfoWindowContent = (repeater: any, farmId: number) => {
    const heightIcon = `<svg class="MuiSvgIcon-root MuiSvgIcon-fontSizeSmall css-ehsa8f" focusable="false" aria-hidden="true" viewBox="0 0 24 24" data-testid="HeightIcon" aria-label="fontSize small" style="vertical-align: middle;"><path d="M13 6.99h3L12 3 8 6.99h3v10.02H8L12 21l4-3.99h-3z"></path></svg>`;

    return `<span 
      style="cursor: pointer;"
      onclick="(function (){
        let event = new CustomEvent('navigateToRepeater', { detail: {
          repeaterId: ${repeater.id},
          farmId: ${farmId}
        }});

        window.dispatchEvent(event);
      })()">
      <div style="text-align: center; min-width: 100px">
        <span style="display: block; font-weight: 600; font-size: .7rem; margin: 4px 0; padding: 4px 8px; border-radius: 8px; color: #000;">
          ${repeater.name}
        </span>
        <span style="display: block; font-size: .7rem; margin: 4px 0; padding: 4px 8px; border-radius: 8px; color: #000;">
          ${heightIcon}
          ${repeater.height ? repeater.height : "-"} m
        </span>
      </div>
    </span>`;
  };

  const getMeterSystemStatusColor = (meterSystem: any) => {
    return "#4679ed";
  };

  const generateBauerPivotInfoWindowContent = (
    bauerPivot: any,
    farmId: number
  ) => {
    return `<span
    style="cursor: pointer;"
    onclick="(function (){
      let event = new CustomEvent('navigateToBauerPivot', { detail: {
        pivotId: ${bauerPivot.id},
        farmId: ${farmId}
      }});

      window.dispatchEvent(event);
    })()">
      <span style='font-weight:600;font-size:.7rem;margin:4px 0 4px 8px;padding:4px 8px;border-radius:8px;color:#000;display:flex;flex-direction:row;flex-wrap:wrap;'>
        ${bauerPivot.name}
      </span>
          <div
            style='background-color:${colorStatus(
              bauerPivot.latest?.status
            )};font-weight:600;font-size:.7rem;margin:4px 0 4px 8px;padding:4px 8px;border-radius:8px;color:#fff;display:flex;flex-direction:row;flex-wrap:wrap;'
              >
              ${bauerPivotStatus(bauerPivot.latest?.status || -1).toUpperCase()}
            </div>
    </span>`;
  };

  useEffect(() => {
    if (pivotID) {
      setDisableQuickSelect(true);
      setTimeout(() => {
        setDisableQuickSelect(false);
      }, 1000);
    }
  }, [pivotID]);

  async function doMapDraw() {
    if (googleMapRef.current === undefined) return;
    setMapShapes([]);
    setCurrentDrawing(true);
    let tempMapShapesPivots: MapShape[] = [];
    let tempMapShapesBauerPivots: MapShape[] = [];
    let tempMapShapesIrpds: MapShape[] = [];
    let tempMapShapesMeterSystems: MapShape[] = [];
    let tempMapShapesRepeaters: MapShape[] = [];
    if (mapShapes.length > 0) {
      mapShapes.forEach((shape) => {
        if (!shape) return;
        if (shape.type === "PivotShape") {
          shape.circle.setMap(null);
          shape.line.setMap(null);
          shape.triangle.setMap(null);
          shape.arrow.setMap(null);
          shape.sector && shape.sector.setMap(null);
          shape.dashedLine && shape.dashedLine.setMap(null);

          shape.semiCircle.forEach((s) => s.setMap(null));
        }
        if (shape.type === "BauerPivotShape") {
          shape.circle.setMap(null);
          shape.line.setMap(null);
          shape.triangle.setMap(null);
          shape.arrow.setMap(null);
          shape.sector && shape.sector.setMap(null);
        }
        if (shape.type === "LinearPivotShape") {
          shape.line.setMap(null);
          shape.circle.setMap(null);
          shape.rectangles.forEach((rectangle) => rectangle.setMap(null));
          shape.arrow.setMap(null);
        }
      });
    }

    if (pivotsAlreadyLoadedLength < totalPivotLength) return;

    if (pivotsCompleted != undefined) {
      tempMapShapesPivots = addPivotsOnMap(
        googleMapRef,
        pivotsCompleted,
        mapShapes,
        latestPanelStream,
        latestGPSStream,
        streamStatusComplete,
        farmID
      );
    }

    if (bauerPivots != undefined) {
      tempMapShapesBauerPivots = addBauerPivotsOnMap(
        googleMapRef,
        bauerPivots,
        mapShapes,
        farmID,
        infoWindow
      );
    }

    if (irpds != undefined) {
      tempMapShapesIrpds = await addIrpdOnMap(googleMapRef, irpds, farmID);
    }

    if (meterSystems != undefined) {
      tempMapShapesMeterSystems = await addMeterSystemsOnMap(
        googleMapRef,
        meterSystems,
        farmID
      );
    }

    if (repeaters != undefined) {
      tempMapShapesRepeaters = await addRepeaterOnMap(
        googleMapRef,
        repeaters,
        farmID
      );
    }

    const tempMapShapes = await tempMapShapesPivots.concat(
      tempMapShapesIrpds,
      tempMapShapesBauerPivots,
      tempMapShapesMeterSystems,
      tempMapShapesRepeaters
    );

    setMapShapes(tempMapShapes);
    setCurrentDrawing(false);
  }

  useEffect(() => {
    if (props.isImperativeControlled || currentDrawing) return;

    if (mapShapes.length > 0) {
      updateDevicesWithStreamData();
    }
  }, [
    googleMapRef.current,
    streamStatusComplete,
    irpds,
    meterSystems,
    meterSystemsStatusKey,
    pivotsCompleted,
    bauerPivots,
    bauerPivotsStatusKey,
    mapShapes.length,
    updateDevicesWithStreamData,
  ]);

  if (pivotsAlreadyLoadedLength < totalPivotLength || disableQuickSelect) {
    return (
      <div
        style={{
          width: "100%",
          height: "100%",
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
        }}
      >
        <CircularProgress color="primary" />
      </div>
    );
  }

  return (
    <Map
      draggable={draggable}
      bounds={bounds}
      onGoogleApiLoaded={handleOnGoogleApiLoaded}
      disableUI={disableUI}
      disableFullScreen={props.disableFullScreen}
      clickOnMapToToggleFullscreen={props.clickOnMapToToggleFullscreen}
      zoomScroll={zoomScroll}
      children={undefined}
      zoom={zoom}
    />
  );
}

export default forwardRef<FarmMapHandle, Props>(FarmMap);
